import { serverTimestamp, type FieldValue } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getDocument,
  getCollection,
  setDocument,
  updateDocument,
  deleteDocument,
  orderBy,
} from '@/lib/firebase/firestore'
import type { MailingListMemberDocument, MailingListSource } from '@/types'
import type { MailingListMemberFormValues } from '@/features/mailing-list'
import type { ParsedEmailEntry } from '@/lib/csv'
import { formatUserFacingError } from '@/lib/errors'

// Email is the document ID — consistent with the public service.
const memberRef = (email: string) =>
  typedDoc<MailingListMemberDocument>('mailingListMembers', email)

const col = () => typedCollection<MailingListMemberDocument>('mailingListMembers')

type MemberCreatePayload = Omit<MailingListMemberDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

export interface BulkAddResult {
  added: number
  skipped: number
  errors: string[]
}

export const adminMailingListService = {
  async getAll(): Promise<Array<MailingListMemberDocument & { id: string }>> {
    return getCollection(col(), orderBy('createdAt', 'desc'))
  },

  async addMember(
    data: MailingListMemberFormValues,
    source: MailingListSource = 'admin-manual',
  ): Promise<string> {
    const normalized = data.email.trim().toLowerCase()
    const ref = memberRef(normalized)
    const existing = await getDocument(ref)  // O(1) direct get, no list query

    if (existing) {
      await updateDocument(ref, {
        firstName: data.firstName.trim() || null,
        lastName: data.lastName.trim() || null,
        segments: data.segments,
        isSubscribed: true,
        unsubscribedAt: null,
        updatedAt: serverTimestamp(),
      })
      return normalized
    }

    const payload: MemberCreatePayload = {
      email: normalized,
      firstName: data.firstName.trim() || null,
      lastName: data.lastName.trim() || null,
      segments: data.segments,
      source,
      isSubscribed: true,
      unsubscribedAt: null,
      userId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    await setDocument(ref, payload as unknown as MailingListMemberDocument)
    return normalized
  },

  async bulkAdd(entries: ParsedEmailEntry[], source: MailingListSource): Promise<BulkAddResult> {
    const result: BulkAddResult = { added: 0, skipped: 0, errors: [] }
    const validEntries = entries.filter((e) => e.isValid)

    const outcomes = await Promise.allSettled(
      validEntries.map(async (entry) => {
        const normalized = entry.email.trim().toLowerCase()
        const ref = memberRef(normalized)
        const existing = await getDocument(ref)  // O(1) direct get

        if (existing) {
          result.skipped++
          return
        }

        const entrySegments = entry.segments?.filter(Boolean) as import('@/types').MailingListSegment[] | undefined
        const payload: MemberCreatePayload = {
          email: normalized,
          firstName: entry.firstName.trim() || null,
          lastName: entry.lastName.trim() || null,
          language: entry.language ?? null,
          segments: entrySegments?.length ? entrySegments : ['general'],
          source,
          isSubscribed: true,
          unsubscribedAt: null,
          userId: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
        await setDocument(ref, payload as unknown as MailingListMemberDocument)
        result.added++
      }),
    )

    outcomes.forEach((outcome, i) => {
      if (outcome.status === 'rejected') {
        result.errors.push(
          `${validEntries[i]?.email ?? 'unknown'}: ${formatUserFacingError(
            outcome.reason,
            'We could not add this subscriber. Please try again.',
            'mailing-list/import',
          )}`,
        )
      }
    })

    result.skipped += entries.length - validEntries.length

    return result
  },

  async updateMember(
    id: string,
    data: Partial<MailingListMemberFormValues & { isSubscribed: boolean }>,
  ): Promise<void> {
    const updates = { updatedAt: serverTimestamp() } as unknown as Partial<MailingListMemberDocument>
    if (data.firstName !== undefined) updates.firstName = data.firstName.trim() || null
    if (data.lastName !== undefined) updates.lastName = data.lastName.trim() || null
    if (data.segments !== undefined) updates.segments = data.segments
    if (data.isSubscribed !== undefined) updates.isSubscribed = data.isSubscribed
    await updateDocument(memberRef(id), updates)
  },

  async deleteMember(id: string): Promise<void> {
    await deleteDocument(memberRef(id))
  },
}
